import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { StaffOrganization } from '@/types';
import { registerStoreReset } from './registry';

interface StaffState {
  /**
   * When set, all API requests will include X-Staff-Org-Override header.
   * The backend swaps the user's organization for the duration of each request.
   */
  orgOverride: StaffOrganization | null;

  // Actions
  setOrgOverride: (org: StaffOrganization | null) => void;
  clearOrgOverride: () => void;
}

export const useStaffStore = create<StaffState>()(
  persist(
    (set) => ({
      orgOverride: null,

      setOrgOverride: (org) => set({ orgOverride: org }),
      clearOrgOverride: () => set({ orgOverride: null }),
    }),
    {
      name: 'staff-storage',
      partialize: (state) => ({
        orgOverride: state.orgOverride,
      }),
    }
  )
);

// Bug-hunt #84: clear org-override on global logout so a fresh login
// doesn't inherit prior impersonation. The cached org list lives in
// TanStack Query and is cleared by the logout-side queryClient.clear()
// (clearSession in src/store/registry.ts).
registerStoreReset(() => {
  useStaffStore.setState({ orgOverride: null });
}, { onUserSwitch: true }); // resets on a switch too (#1336): orgOverride belongs to the login that was folded
